import React, { useEffect, useState } from 'react';
import {
  Box, Grid, TextField, Button, TableContainer, Table, TableHead,
  TableRow, TableCell, TableBody, Paper, Typography, TablePagination,
  Dialog, DialogTitle, DialogContent, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddStudentFeeForm from './AddStudentFeeForm';
import { API_GET_FEES } from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';

const FeeManagement = () => {
  const [fees, setFees] = useState([]);
  const [searchMonth, setSearchMonth] = useState('');
  const [searchClass, setSearchClass] = useState('');
  const [searchStudent, setSearchStudent] = useState('');
  const [searchFather, setSearchFather] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog control
  const [open, setOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null); // 🔥 for editing
  const { logout } = useAuth();

  const getAllFees = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_GET_FEES, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        logout();
        return;
      }

      const responseData = await response.json();
      const data = responseData.data || [];
      setFees(data);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  useEffect(() => {
    getAllFees();
  }, []);

  const filtered = fees.filter(f => {
    return (
      (!searchMonth || f.fee_month?.startsWith(searchMonth)) &&
      (!searchClass || f.student_class?.toLowerCase().includes(searchClass.toLowerCase())) &&
      (!searchStudent || f.student_name?.toLowerCase().includes(searchStudent.toLowerCase())) &&
      (!searchFather || f.student_father_name?.toLowerCase().includes(searchFather.toLowerCase()))
    );
  });

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRows = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleOpenAdd = () => {
    setSelectedFee(null); // new record
    setOpen(true);
  };

  const handleOpenEdit = (fee) => {
    setSelectedFee(fee); // editing record
    setOpen(true);
  };

  return (
    <Box p={2}>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h5">Fee List</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleOpenAdd}>Add Fee</Button>
        </Grid>
      </Grid>

      {/* 🔥 Filters */}
      <Grid container mb={2} spacing={2}>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Search Month"
            type="month"
            value={searchMonth}
            onChange={e => { setSearchMonth(e.target.value); setPage(0); }}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Search Class"
            value={searchClass}
            onChange={e => { setSearchClass(e.target.value); setPage(0); }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Search Student Name"
            value={searchStudent}
            onChange={e => { setSearchStudent(e.target.value); setPage(0); }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Search Father Name"
            value={searchFather}
            onChange={e => { setSearchFather(e.target.value); setPage(0); }}
            fullWidth
          />
        </Grid>
      </Grid>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Student No</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Father Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Month</TableCell>
                <TableCell>Tuition</TableCell>
                <TableCell>Magic</TableCell>
                <TableCell>Fee Pending</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Action</TableCell> {/* 🔥 New column */}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((f, idx) => (
                <TableRow key={f.id || idx}>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{f.student_no} </TableCell>
                  <TableCell>{f.student_name} </TableCell>
                  <TableCell>{f.student_father_name} </TableCell>
                  <TableCell>{f.class || '-'}</TableCell>
                  <TableCell>{f.fee_month?.substring(0,7)}</TableCell>
                  <TableCell>{f.tuition_charge}</TableCell>
                  <TableCell>{f.magic_charge || '-'}</TableCell>
                  <TableCell>{f.fee_pending || '-'}</TableCell>
                  <TableCell>{f.description || '-'}</TableCell>
                  <TableCell>{f.created_at?.split('T')[0]}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenEdit(f)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">No fees found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRows}
          rowsPerPageOptions={[5,10,20]}
        />
      </Paper>

      {/* Add/Edit Fee Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          {selectedFee ? "Edit Fee" : "Add Fee"}
          <IconButton onClick={() => setOpen(false)} style={{width:'100px'}}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <AddStudentFeeForm
            initialData={selectedFee}   // 🔥 pass data for editing
            mode={selectedFee ? "edit" : "add"}
            onSuccess={() => { getAllFees(); setOpen(false); }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FeeManagement;
